import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../components/ui/Modal';
import Icon from '../../../components/AppIcon';
import MetricCard from '../../reporting-and-analytics-dashboard/components/MetricCard';
import ChartWidget from '../../reporting-and-analytics-dashboard/components/ChartWidget';
import { monitoringService } from '../../../services/monitoringService';
import { useWorkflowJobsRealtime } from '../../../hooks/useWorkflowJobsRealtime';
import { useAuth } from '../../../contexts/AuthContext';

const WorkflowMonitoringDashboard = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [processingTrends, setProcessingTrends] = useState([]);
  const [successRates, setSuccessRates] = useState([]);
  const [failureAnalysis, setFailureAnalysis] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Load all dashboard data
  const loadDashboardData = useCallback(async () => {
    if (!isOpen) return;
    
    setIsLoading(true);
    try {
      const [metricsData, trendsData, successRatesData, failureData, activityData] = await Promise.all([
        monitoringService?.getDashboardMetrics(),
        monitoringService?.getProcessingTimeTrends(),
        monitoringService?.getSuccessRateByJobType(),
        monitoringService?.getFailureAnalysis(),
        monitoringService?.getRecentJobActivity(15)
      ]);

      setMetrics(metricsData);
      setProcessingTrends(trendsData);
      setSuccessRates(successRatesData);
      setFailureAnalysis(failureData);
      setRecentActivity(activityData);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen]);

  // Initial load
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Real-time updates handler
  const handleJobUpdate = useCallback((eventType, jobData) => {
    console.log('Real-time job update:', eventType, jobData);
    // Reload dashboard data on any job change
    loadDashboardData();
  }, [loadDashboardData]);

  // Subscribe to real-time updates
  useWorkflowJobsRealtime(user?.id, handleJobUpdate);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10';
      case 'running':
        return 'text-primary bg-primary/10';
      case 'failed':
        return 'text-error bg-error/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'معلق',
      running: 'جاري التنفيذ',
      completed: 'مكتمل',
      failed: 'فشل',
      cancelled: 'ملغى'
    };
    return labels?.[status] || status;
  };

  const getJobTypeLabel = (jobType) => {
    const labels = {
      batch_contract_processing: 'معالجة دفعة العقود',
      bulk_document_generation: 'إنشاء مستندات متعددة',
      automated_follow_up: 'جدولة متابعات تلقائية',
      signature_reminder_batch: 'تذكيرات التوقيع',
      contract_review_batch: 'مراجعة العقود'
    };
    return labels?.[jobType] || jobType;
  };

  // Prepare chart data
  const successRateChartData = successRates?.map(item => ({
    name: getJobTypeLabel(item?.jobType),
    successRate: item?.successRate,
    total: item?.total
  }));

  const processingTimeChartData = processingTrends?.map(item => ({
    date: item?.date,
    avgTime: item?.avgTime
  }));

  const failureDistributionData = failureAnalysis?.failureDistribution?.map(item => ({
    name: item?.category,
    value: item?.count
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="لوحة مراقبة الأتمتة في الوقت الفعلي"
      size="full"
      footer={null}
      icon="Activity"
      iconColor="var(--color-primary)"
    >
      <div className="space-y-6" dir="rtl">
        {/* Real-time indicator */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span className="text-xs text-muted-foreground">
              آخر تحديث: {lastUpdate?.toLocaleTimeString('ar-JO')}
            </span>
          </div>
          <button
            onClick={loadDashboardData}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-muted hover:bg-muted/80 rounded-md transition-colors"
          >
            <Icon name="RefreshCw" size={14} />
            تحديث
          </button>
        </div>

        {isLoading && !metrics ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin" />
          </div>
        ) : (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                title="معدل النجاح"
                value={`${metrics?.successRate || 0}%`}
                change={metrics?.successRate >= 80 ? '+5%' : '-2%'}
                changeType={metrics?.successRate >= 80 ? 'positive' : 'negative'}
                icon="CheckCircle"
                loading={isLoading}
              />
              <MetricCard
                title="متوسط وقت المعالجة"
                value={`${metrics?.avgProcessingTime || 0} دقيقة`}
                change="-8%"
                changeType="positive"
                icon="Clock"
                loading={isLoading}
              />
              <MetricCard
                title="توفير التكاليف"
                value={`${metrics?.totalCostSavings || 0} ساعة`}
                change="+15%"
                changeType="positive"
                icon="TrendingUp"
                loading={isLoading}
              />
              <MetricCard
                title="إجمالي العناصر المعالجة"
                value={metrics?.totalItemsProcessed || 0}
                change={`${metrics?.totalItemsFailed || 0} فشل`}
                changeType={metrics?.totalItemsFailed > 0 ? 'negative' : 'positive'}
                icon="Package"
                loading={isLoading}
              />
            </div>

            {/* Status Overview */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-card border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Layers" size={16} color="var(--color-primary)" />
                  <span className="text-xs text-muted-foreground">إجمالي المهام</span>
                </div>
                <p className="text-2xl font-bold text-foreground">{metrics?.totalJobs || 0}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                  <span className="text-xs text-muted-foreground">مكتملة</span>
                </div>
                <p className="text-2xl font-bold text-success">{metrics?.completedJobs || 0}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Loader2" size={16} color="var(--color-primary)" />
                  <span className="text-xs text-muted-foreground">قيد التنفيذ</span>
                </div>
                <p className="text-2xl font-bold text-primary">{metrics?.runningJobs || 0}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Clock" size={16} color="var(--color-warning)" />
                  <span className="text-xs text-muted-foreground">معلقة</span>
                </div>
                <p className="text-2xl font-bold text-warning">{metrics?.pendingJobs || 0}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="XCircle" size={16} color="var(--color-error)" />
                  <span className="text-xs text-muted-foreground">فاشلة</span>
                </div>
                <p className="text-2xl font-bold text-error">{metrics?.failedJobs || 0}</p>
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Processing Time Trends */}
              <ChartWidget
                title="اتجاهات وقت المعالجة (آخر 7 أيام)"
                type="line"
                data={processingTimeChartData}
                dataKey="avgTime"
                xAxisKey="date"
                colors={['var(--color-primary)']}
                loading={isLoading}
                onExport={() => {}}
                onFullscreen={() => {}}
              />

              {/* Success Rate by Job Type */}
              <ChartWidget
                title="معدل النجاح حسب نوع المهمة"
                type="bar"
                data={successRateChartData}
                dataKey="successRate"
                xAxisKey="name"
                colors={['var(--color-success)']}
                loading={isLoading}
                onExport={() => {}}
                onFullscreen={() => {}}
              />

              {/* Failure Distribution */}
              <ChartWidget
                title="توزيع الأخطاء"
                type="pie"
                data={failureDistributionData}
                dataKey="value"
                xAxisKey="name"
                colors={['#EF4444', '#F59E0B', '#3B82F6', '#10B981', '#8B5CF6']}
                loading={isLoading}
                onExport={() => {}}
                onFullscreen={() => {}}
              />

              {/* Recent Failures */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
                    الأخطاء الأخيرة
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {failureAnalysis?.totalFailures || 0} إجمالي
                  </span>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-auto">
                  {failureAnalysis?.recentFailures?.length === 0 ? (
                    <div className="text-center py-8">
                      <Icon name="CheckCircle" size={32} color="var(--color-success)" className="mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">لا توجد أخطاء حديثة</p>
                    </div>
                  ) : (
                    failureAnalysis?.recentFailures?.map((failure) => (
                      <div key={failure?.id} className="p-3 rounded-lg bg-error/5 border border-error/20">
                        <div className="flex items-start justify-between mb-1">
                          <p className="text-xs font-semibold text-foreground">{failure?.jobName}</p>
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(failure?.createdAt)?.toLocaleDateString('ar-JO')}
                          </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mb-1">
                          {getJobTypeLabel(failure?.jobType)}
                        </p>
                        <p className="text-[10px] text-error line-clamp-2">{failure?.errorLog}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          عناصر فاشلة: {failure?.failedItems}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Activity" size={16} color="var(--color-primary)" />
                النشاط الأخير
              </h3>
              <div className="space-y-2 max-h-[400px] overflow-auto">
                {recentActivity?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="Inbox" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">لا يوجد نشاط حديث</p>
                  </div>
                ) : (
                  recentActivity?.map((job) => {
                    const progress = job?.totalItems > 0 ? (job?.processedItems / job?.totalItems) * 100 : 0;
                    return (
                      <div key={job?.id} className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-xs font-semibold text-foreground">{job?.jobName}</p>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(job?.status)}`}>
                                {getStatusLabel(job?.status)}
                              </span>
                            </div>
                            <p className="text-[10px] text-muted-foreground">
                              {getJobTypeLabel(job?.jobType)} • {new Date(job?.createdAt)?.toLocaleString('ar-JO')}
                            </p>
                          </div>
                        </div>
                        {job?.status === 'running' && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                              <span>التقدم: {job?.processedItems} / {job?.totalItems}</span>
                              <span>{Math.round(progress)}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary transition-all duration-300"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default WorkflowMonitoringDashboard;